import { Component } from 'preact';
import { useRef, useEffect, useState, useMemo } from 'preact/hooks';

/**
 * Questo modulo esporta useHeadlessComponent.
 * Questo hook serve a convertire i class component in oggetti con solo business logic e renderizzare separatamnte.
 * Approccio simile ad estendere il componente, ma con meno mal di testa.
 */

// Chiavi interne o di lifecycle di Preact che ignoreremo nell'esportazione
type PreactReservedKeys =
    | 'render'
    | 'componentWillMount'
    | 'componentDidMount'
    | 'componentWillUnmount'
    | 'componentWillReceiveProps'
    | 'shouldComponentUpdate'
    | 'componentWillUpdate'
    | 'componentDidUpdate'
    | 'componentDidCatch'
    | 'setState'
    | 'forceUpdate'
    | 'context'
    | 'props'
    | 'state'
    | 'base'
    | 'constructor';

// Estrae solo i metodi custom dalla classe (ignorando property non-funzione e lifecycle)
type CustomMethods<T> = {
    [K in keyof T as K extends PreactReservedKeys
        ? never
        : T[K] extends Function
        ? K
        : never]: T[K];
};

// La forma dell'oggetto finale restituito dall'hook
export type HeadlessApi<TInstance, TProps, TState> = {
    state: TState;
    props: TProps;
} & CustomMethods<TInstance>;

export function useHeadlessComponent<
    P extends Record<string, any>,
    S extends Record<string, any>,
    C extends new (props: P) => Component<P, S>
>(
    ClassComp: C,
    props: P
): HeadlessApi<InstanceType<C>, P, S> {
    const [, forceUpdate] = useState({});
    const instanceRef = useRef<InstanceType<C> | null>(null);

    // 1. INIZIALIZZAZIONE
    if (!instanceRef.current) {
        const instance = new ClassComp(props) as InstanceType<C>;
        instance.props = props;
        
        // Gestione fallback per state undefined
        if (!instance.state) {
            instance.state = {} as S;
        }

        // Sovrascrittura tipizzata di setState
        instance.setState = (
            updater: Partial<S> | ((prevState: S, props: P) => Partial<S> | null),
            callback?: () => void
        ) => {
            const nextState = typeof updater === 'function'
                ? updater(instance.state as S, instance.props as P)
                : updater;

            if (nextState) {
                instance.state = { ...instance.state, ...nextState } as S;
            }

            forceUpdate({});
            if (callback) callback();
        };

        // Lifecycle legacy (lo forziamo ad any per poterlo chiamare se esiste)
        if (typeof (instance as any).componentWillMount === 'function') {
            (instance as any).componentWillMount();
        }

        instanceRef.current = instance;
    }

    const instance = instanceRef.current;

    // 2. GESTIONE PROPS
    const prevProps = useRef(props);
    useEffect(() => {
        if (prevProps.current !== props) {
            if (typeof (instance as any).componentWillReceiveProps === 'function') {
                (instance as any).componentWillReceiveProps(props);
            }
            instance.props = props;
            prevProps.current = props;
        }
    }, [props, instance]);

    // 3. GESTIONE MOUNT / UNMOUNT
    useEffect(() => {
        if (typeof (instance as any).componentDidMount === 'function') {
            (instance as any).componentDidMount();
        }

        return () => {
            if (typeof (instance as any).componentWillUnmount === 'function') {
                (instance as any).componentWillUnmount();
            }
        };
    }, [instance]);

    // 4. ESTRAZIONE DINAMICA PER IL RITORNO
    const headlessApi = useMemo(() => {
        const api: any = {
            state: instance.state,
            props: instance.props,
        };

        const proto = Object.getPrototypeOf(instance);
        const methodNames = Object.getOwnPropertyNames(proto);

        methodNames.forEach((name) => {
            if (
                name !== 'constructor' &&
                name !== 'render' &&
                typeof (instance as any)[name] === 'function'
            ) {
                api[name] = (instance as any)[name].bind(instance);
            }
        });

        // Metodi scritti come arrow functions
        Object.keys(instance).forEach((key) => {
            if (typeof (instance as any)[key] === 'function') {
                api[key] = (instance as any)[key];
            }
        });

        // Forziamo il tipo in uscita perché TS non può inferire Object.keys dinamicamente
        return new Proxy(api, {
            set(target, prop, value) {
                target[prop] = value;
                (instance as any)[prop] = value;
                return true;
            }
        }) as HeadlessApi<InstanceType<C>, P, S>;
    }, [instance, instance.state, instance.props]);

    return headlessApi;
}